import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportJobsByContact from "hooks/organization/reports/types/useGetReportJobsByContact";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableJobsByContact(startDate, endDate, companyId = null) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportJobsByContact, { data, loading, error }] = useGetReportJobsByContact(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        companyId,
        page,
        first,
      };
      getReportJobsByContact({ variables });
    },
    [startDate, endDate, companyId, getReportJobsByContact]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportJobsByContact?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Customer Name",
        accessor: "customerName",
        Cell: ({ value }) => value || "",
        filter: "includes",
      },
      {
        Header: "Job",
        accessor: "job",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job Size",
        accessor: "jobSize",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Total",
        accessor: "total",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportJobsByContact?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportJobsByContact: paginate,
    metadata: data?.getReportJobsByContact?.metadata,
  } as const;
}
